import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { privacyPolicy } from '../../data/privacyPolicy';
interface PrivacyModalProps {
  open: boolean;
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function PrivacyModal({
  open,
  onClose
}: PrivacyModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-23" data-mp="3m2e0xtf3isca">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-24" data-mp="3m3fcdz00xguj">
          <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" data-id="element-25" data-mp="3h2rnbivcva2y" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="privacy-title" initial={{
        opacity: 0,
        y: 16,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease
      }} className="relative flex max-h-[88svh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-[#1E1E24] text-left text-white shadow-2xl sm:rounded-2xl" data-id="element-26" data-mp="33c5zdfr1o9y7">
            <div className="h-0.5 w-full bg-[#FF101F]" aria-hidden="true" data-id="element-27" data-mp="2patpnm2h94kr" />

            <header className="flex items-start justify-between gap-6 border-b border-white/10 px-6 py-5 sm:px-8 sm:py-6" data-id="element-28" data-mp="uw69omq54kk3">
              <div data-id="element-29" data-mp="q3mz7ymr25ji">
                {privacyPolicy.isDraft && <span className="mb-3 inline-block rounded-full border border-[#FF101F]/60 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[#FF101F]" data-id="element-30" data-mp="l5bollomrr08">
                    Borrador
                  </span>}
                <h2 id="privacy-title" className="text-2xl font-bold tracking-tight sm:text-3xl" data-id="element-31" data-mp="1dsob5vusjytj">
                  {privacyPolicy.title}
                </h2>
                <p className="mt-1.5 text-xs text-white/50" data-id="element-32" data-mp="14t19ra22at0h">{privacyPolicy.updated}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar política de privacidad" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 text-white transition-colors duration-150 ease-out hover:border-[#FF101F] hover:text-[#FF101F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF101F]" data-id="element-33" data-mp="zty5vsxtisu3">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-34" data-mp="293m6mnpizpr3" />
              </button>
            </header>

            <div className="overflow-y-auto overscroll-contain px-6 py-6 sm:px-8 sm:py-8" data-id="element-35" data-mp="2dvmdz17xdkxp">
              <p className="leading-relaxed text-white/80" data-id="element-36" data-mp="2iugutxmc0zf6">{privacyPolicy.intro}</p>

              <div className="mt-8 space-y-8" data-id="element-37" data-mp="2nsig8p78datt">
                {privacyPolicy.sections.map((s, i) => <section key={s.heading} data-id="element-38" data-mp="2srbu4iqfj3ui" data-mpi={i}>
                    <h3 className="flex items-baseline gap-3 text-lg font-semibold" data-id="element-39" data-mp="2ssmue8ko1tgv">
                      <span className="text-sm font-bold text-[#FF101F]" data-id="element-40" data-mp="2ws5cyxp0lsgj">{String(i + 1).padStart(2, '0')}</span>
                      {s.heading}
                    </h3>
                    {'paragraphs' in s && s.paragraphs.map(p => <p key={p} className="mt-3 text-[0.95rem] leading-relaxed text-white/75" data-id="element-41" data-mp="74psdkt63b5d">
                          {p}
                        </p>)}
                    {'list' in s && <ul className="mt-3 space-y-2" data-id="element-42" data-mp="h2doae2ieop4">
                        {s.list.map(item => <li key={item} className="flex gap-3 text-[0.95rem] leading-relaxed text-white/75" data-id="element-43" data-mp="c5dg9xm3nbvd">
                            <span className="mt-[0.6rem] h-1 w-1 shrink-0 rounded-full bg-[#FF101F]" aria-hidden="true" data-id="element-44" data-mp="3kkouay36dcw2" />
                            {item}
                          </li>)}
                      </ul>}
                    {'after' in s && <p className="mt-3 text-[0.95rem] leading-relaxed text-white/75" data-id="element-45" data-mp="3phnofgmu9ju9">{s.after}</p>}
                  </section>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}